import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState, useRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faLockOpen } from '@fortawesome/pro-duotone-svg-icons';
import { userAtom, notificationsAtom, tokenAtom } from '../../../atoms/Atoms';
import { H1, H2, H4 } from '../../shared-components/typography/Title';
import Button from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import Input from '../../shared-components/form/Input';
import api from '../../../api/api';
import { encryptString } from '../../../functions';
import envConfig from '../../../envConfig';
import MainPageAuth from '../../shared-components/main-page/MainPageAuth';

function Login() {
    const [notifications, setNotifications] = useRecoilState(notificationsAtom);
    const { t } = useTranslation();
    const history = useHistory();
    const [user, setUser] = useRecoilState(userAtom);
    const setToken = useSetRecoilState(tokenAtom);
    const [loading, setLoading] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
      if (!loggingIn && user) {
        window.location = '/';
      }
    }, [user]);

    function login(values) {
      setLoading(true);
      const payload = {
        Username: values.email,
        Password: encryptString(values.password)
      };
      api.post('auth/login', payload)
        .then((response) => {
          setLoading(false);
          if (response?.data?.success) {
            if (!response.data.data.profile.acceptedOn) {
              setNotifications([
                ...notifications,
                {
                  title: t('error'),
                  description: t('your_account_not_approved_yet'),
                  error: true,
                },
              ]);
            } else if (response.data.data.profile.isBlocked) {
              setNotifications([
                ...notifications,
                {
                  title: t('error'),
                  description: t('your_account_is_blocked'),
                  error: true,
                },
              ]);
            } else {
              setLoggingIn(true);
              localStorage.setItem(
                `${envConfig.StoragePrefix}-user`,
                JSON.stringify(response.data.data.profile)
              );
              localStorage.setItem(
                `${envConfig.StoragePrefix}-token`,
                response.data.data.accessToken
              );
              localStorage.setItem(
                `${envConfig.StoragePrefix}-token-date`,
                dayjs().toString()
              );
              setToken(response.data.data.accessToken);
              localStorage.setItem(`${envConfig.StoragePrefix}-impersonate`, false);
              return response;
            }
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response?.data?.error,
                error: true,
              },
            ]);
          }
        })
        .then((response) => {
          if (response) {
            setUser(response.data.data.profile);
            if (response.data.data.profile.forcePasswordReset) {
              history.push('/updatepass');
            } else {
              history.push('/');
            }
          }
        })
        .catch((error) => {
          setLoading(false);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        });
    }

    const formik = useFormik({
      initialValues: {
        email: '',
        password: ''
      },
      validationSchema: Yup.object().shape({
        email: Yup.string()
          .email(t('valid_email_validation'))
          .required(t('required_field')),
        password: Yup.string().required(t('required_field')),
      }),
      onSubmit: (values, { setSubmitting }) => login(values),
    });

    return (
      <MainPageAuth>
        <div className="bg-solar2 bg-cover bg-center w-full mt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 px-12 mb-4">
          <div className="flex col-span-1 lg:col-span-4 flex-col w-full mt-12 md:mt-48 justify-center items-center">
            <H1 className="hidden my-4 bg-secondaryColor text-secondaryTextColor bg-opacity-90 px-8 py-4 text-center" text={`${t('welcome_message')} - ${envConfig.SiteTitle}`} />
          </div>
          <div className="flex md:col-span-1 bg-secondaryColor text-secondaryTextColor my-8 md:m-4 bg-opacity-90 lg:col-span-2 flex-col w-full md:mt-48 justify-center items-center">
            <H2 className="my-4 text-center" text={t('login_to_interact')} />
            <Form className="mt-4 w-10/12">
              <Input
                label={t('email')}
                required
                type="email"
                name="email"
                id="email"
                className="mb-8"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onKeyDown={(e) => {
                  if (e.which === 13) {
                    e.preventDefault();
                    formik.submitForm();
                  }
                }}
                error={
                  formik.errors.email && formik.touched.email && formik.errors.email
                }
              />
              <Input
                label={t('password')}
                required
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                suffix={(
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                  </button>
                )}
                className="mb-8"
                autoComplete="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onKeyDown={(e) => {
                  if (e.which === 13) {
                    e.preventDefault();
                    formik.submitForm();
                  }
                }}
                error={
                  formik.errors.password &&
                  formik.touched.password &&
                  formik.errors.password
                }
              />
            </Form>
            <Button
              className="bg-primaryColor mb-6 w-9/12"
              textColor="text-primaryTextColor"
              loaderColor="bg-primaryTextColor"
              borderColor="border-primaryBorderColor"
              loading={loading}
              text={t('sign_in')}
              onClick={formik.submitForm}
            />
            <Button
              disabled={loading}
              className="bg-accentColor mb-6 w-9/12"
              textColor="text-accentTextColor"
              borderColor="border-accentBorderColor"
              text={t('create_account')}
              onClick={() => history.push('/register')}
            />
            <Button
              disabled={loading}
              className="mb-6 w-9/12"
              textColor="text-secondaryTextColor"
              text={t('forgot_password')}
              icon={faLockOpen}
              onClick={() => history.push('/recover')}
            />
            <H4 className="mb-4" text={t('or')} />
            <Button
              disabled={loading}
              className="bg-accentColor mb-6 w-9/12"
              textColor="text-accentTextColor"
              borderColor="border-accentBorderColor"
              text={t('return_home')}
              onClick={() => history.push('/')}
            />
          </div>
        </div>
      </MainPageAuth>
    );
}

export default Login;
